/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  HeaderTitle,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableCellEmail,
  TableWrapper,
  TwoColumns,
} from '../../../services/settings/appStyle/global.style';
import { UserContainer } from './user.style';
import {
  Loader,
  Table,
  DropdownButton,
  IconButton as IconButtonWithText,
} from '../../../Components/uielements';
import { useUserContext } from '../../../hooks/UserContext';
import { IntlMessages } from '../../../helper/langHelper';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import { exportExcelFile } from '../../../helper/exportExcelHelper';
import moment from 'moment';
import { convertToUserTimeZoneDateTime } from '../../../helper/dataFormat';
import { getManagerUsersList } from '../../../services/bioplus-services/user';
import { AddPlusIcon } from '../../../Components/icons';
import { InviteUserModal } from './modals';

const ManagerRoles = () => {
  const userContext = useUserContext();
  const companyId = userContext?.company?.companyId?.id;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isAddUserModal, setIsAddUserModal] = useState(false);

  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find((permission) => permission?.label === 'user');

  const canDownloadReport = !!isUserAllowToManage?.isReport;
  // const canManageUser = !!isUserAllowToManage?.isManage;
  const canCreateUser = !!isUserAllowToManage?.isCreate;

  const usersQuery = useQuery(['managerRoles', companyId, page, pageSize], () =>
    getManagerUsersList(companyId, page, pageSize)
  );
  const userTableData = usersQuery?.data?.data ?? [];
  const userTimeZone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  function extractData(dataArray) {
    if (dataArray) {
      const extractedData = dataArray?.map((item) => {
        return {
          Name: `${item?.userId?.firstName} ${item?.userId?.lastName}`,
          accountManager: item?.companyId?.name,
          email: item?.userId?.email,
          role: item?.label,
          mobile: item?.userId?.phone,
          createdAt: moment(item?.createdAt).format('DD-MM-YYYY'),
          lastLogin: item?.userId?.lastLogin
            ? convertToUserTimeZoneDateTime(item?.userId?.lastLogin, userTimeZone)
            : 'N/A',
        };
      });
      return { bioplusUsers: extractedData };
    }
  }

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };
  const handleClosedAddUserModal = () => {
    setIsAddUserModal(false);
  };

  //   table coulmns
  const userColumns = useMemo(
    () => [
      {
        Header: <IntlMessages id="table.col.name" />,
        accessor: 'userName',
        Cell: (cellData) => (
          <TableCell>
            {`${cellData?.row?.original?.userId?.firstName} ${cellData?.row?.original?.userId?.lastName}`}
          </TableCell>
        ),
        width: '16%',
      },
      {
        Header: <IntlMessages id="table.col.account.owner" />,
        accessor: 'companyName',
        width: '16%',
        Cell: (cellData) => <TableCell>{`${cellData?.row?.original?.companyId?.name}`}</TableCell>,
      },
      {
        Header: <IntlMessages id="table.col.email" />,
        accessor: 'email',
        width: '20%',
        Cell: (cellData) => (
          <TableCellEmail>{`${cellData?.row?.original?.userId?.email}`}</TableCellEmail>
        ),
      },
      {
        Header: <IntlMessages id="table.col.role" />,
        accessor: 'label',
      },
      {
        Header: <IntlMessages id="table.col.mobile.no" />,
        accessor: 'phone',
        Cell: (cellData) => <TableCell>{`${cellData?.row?.original?.userId?.phone}`}</TableCell>,
      },
      {
        Header: <IntlMessages id="table.col.createdAt" />,
        accessor: 'createdAt',
        Cell: (cellData) => (
          <TableCell>
            {cellData.value ? moment(cellData.value).format('DD-MM-YYYY') : '--'}
          </TableCell>
        ),
      },
      {
        Header: <IntlMessages id="table.col.last.login" />,
        accessor: 'lastLogin',
        width: '15%',
        Cell: (cellData) => (
          <TableCell>
            {cellData?.row?.original?.userId?.lastLogin
              ? `${convertToUserTimeZoneDateTime(
                  cellData?.row?.original?.userId?.lastLogin,
                  userTimeZone
                )}`
              : 'N/A'}
          </TableCell>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const excelFormatData = extractData(usersQuery?.data?.data);
  const fileName = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.xlsx`;
  const fileNameCsv = `Members-${moment().format('DD-MM-YYYY-hh.mm A')}.csv`;
  const columnConfig = [
    { header: 'Name', key: 'Name', width: 30 },
    { header: 'Account Manager', key: 'accountManager', width: 30 },
    { header: 'Email', key: 'email', width: 30 },
    { header: 'Role', key: 'role', width: 20 },
    { header: 'Mobile No', key: 'mobile', width: 20 },
    { header: 'created At', key: 'createdAt', width: 20 },
    { header: 'Last Login', key: 'lastLogin', width: 20 },
  ];

  const handleClickItem = (item) => {
    switch (item?.key) {
      case 'excel':
        exportExcelFile(excelFormatData, fileName, columnConfig);
        break;
      case 'pdf':
        break;
      case 'csv':
        exportExcelFile(excelFormatData, fileNameCsv, columnConfig);
        break;
      default:
        break;
    }
  };

  if (usersQuery.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }
  return (
    <StyledContainer>
      <UserContainer>
        <HeaderTitle>
          <IntlMessages id="nav.roles" />
        </HeaderTitle>
        <TwoColumns style={{ gap: '15px' }}>
          {' '}
          {canCreateUser && (
            <IconButtonWithText
              text={<IntlMessages id="button.text.add.user" />}
              icon={<AddPlusIcon />}
              onClick={() => {
                setIsAddUserModal(true);
              }}
            />
          )}
          {excelFormatData && canDownloadReport && (
            <DropdownButton
              noOfColoumns={1}
              buttonArray={[
                { key: 'excel', title: 'Export as Excel', icon: <CloudDownloadIcon /> },
                { key: 'csv', title: 'Export as CSV', icon: <SimCardDownloadIcon /> },
              ]}
              onClick={handleClickItem}
            />
          )}
        </TwoColumns>
      </UserContainer>
      <TableWrapper>
        <Table
          data={userTableData}
          columns={userColumns}
          hasFooter={true}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={usersQuery?.data?.total || 0}
        />
      </TableWrapper>{' '}
      {isAddUserModal && canCreateUser && (
        <InviteUserModal onClose={handleClosedAddUserModal} companyId={companyId} />
      )}
    </StyledContainer>
  );
};

export default ManagerRoles;
